import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Globe, ExternalLink, FileText, Loader2, Clock, Eye, TrendingUp, UserCircle, Activity } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatDistanceToNow, format } from "date-fns";
import { fr } from "date-fns/locale";
import { useQuery } from "@tanstack/react-query";
import { useCompany } from "@/hooks/useCompany";
import HostingConnectionPanel from "@/components/websites/HostingConnectionPanel";
import FrontPagePinCard from "@/components/websites/FrontPagePinCard";
import ShowcaseCard from "@/components/websites/ShowcaseCard";

type WebsiteDetail = {
  id: string;
  name: string;
  slug: string;
  domain: string | null;
  tagline: string | null;
  description: string | null;
  status: string;
  niche: string | null;
  languages: string[];
  primary_language: string;
  article_count: number;
  logo_url: string | null;
  cover_image_url: string | null;
  template_id: string | null;
  created_at: string;
  updated_at: string;
  theme_config: Record<string, unknown> | null;
  content_config: Record<string, unknown> | null;
  ai_config: Record<string, unknown> | null;
  external_hosting_provider: string | null;
  publish_status: string | null;
  sync_status: string | null;
  last_publish_at: string | null;
  last_sync_at: string | null;
  last_publish_error: string | null;
  last_sync_error: string | null;
  owner_id: string | null;
  company_id: string | null;
};

type Article = {
  id: string;
  title: string;
  slug: string;
  status: string;
  excerpt: string | null;
  published_at: string | null;
  created_at: string;
  is_featured: boolean | null;
  is_breaking: boolean | null;
  reading_time: number | null;
  author: { full_name: string | null } | null;
  category: { name: string } | null;
};

const detailDb = supabase as any;

const statusColors: Record<string, string> = {
  active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  draft: "bg-muted text-muted-foreground border-border",
  paused: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  published: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  review: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  scheduled: "bg-violet-500/15 text-violet-400 border-violet-500/30",
  archived: "bg-muted text-muted-foreground border-border",
};

export default function WebsiteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentCompanyId, isPlatformOwner, isLoading: companyLoading } = useCompany();
  const { data: website, isLoading: siteLoading } = useQuery({
    queryKey: ["website-detail-page", id, currentCompanyId, isPlatformOwner],
    queryFn: async () => {
      // Defence-in-depth on top of RLS: scope the fetch by company_id
      // unless the caller is the platform owner. Without this, a guessed
      // UUID would 404 via RLS but with a slow path; the explicit filter
      // hits the (company_id, id) index and short-circuits.
      let query = detailDb.from("websites").select("*").eq("id", id);
      if (!isPlatformOwner && currentCompanyId) {
        query = query.eq("company_id", currentCompanyId);
      }
      const { data, error } = await query.single();
      if (error) throw error;
      return data as WebsiteDetail;
    },
    enabled: !!id && !companyLoading && (isPlatformOwner || !!currentCompanyId),
  });

  const { data: ownerName = "Unknown" } = useQuery({
    queryKey: ["website-owner", website?.owner_id],
    queryFn: async () => {
      const { data } = await detailDb.from("profiles").select("full_name").eq("id", website!.owner_id).maybeSingle();
      return data?.full_name || "Unknown";
    },
    enabled: !!website?.owner_id,
  });

  const { data: articles = [] } = useQuery({
    queryKey: ["website-detail-articles", id],
    queryFn: async () => {
      const { data: publications } = await detailDb
        .from("article_publications")
        .select("article_id")
        .eq("website_id", id)
        .limit(100);
      const articleIds = (publications || []).map((publication: any) => publication.article_id);
      if (articleIds.length === 0) return [];
      const { data, error } = await detailDb
        .from("articles")
        .select("id, title, slug, status, excerpt, published_at, created_at, is_featured, is_breaking, reading_time, author_id")
        .in("id", articleIds)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data as Article[];
    },
    enabled: !!id,
  });

  const { data: activityItems = [] } = useQuery({
    queryKey: ["website-activity", id],
    queryFn: async () => {
      const [{ data: publications }, { data: jobs }] = await Promise.all([
        detailDb.from("article_publications").select("id, status, published_at, updated_at, article_id").eq("website_id", id).order("updated_at", { ascending: false }).limit(20),
        detailDb.from("autopublish_jobs").select("id, status, error_message, scheduled_for, completed_at, updated_at").eq("website_id", id).order("updated_at", { ascending: false }).limit(20),
      ]);
      const articleIds = (publications || []).map((item: any) => item.article_id).filter(Boolean);
      const { data: logs } = articleIds.length
        ? await detailDb.from("automation_logs").select("id, step, status, error_message, created_at, article_id").in("article_id", articleIds).order("created_at", { ascending: false }).limit(20)
        : { data: [] };
      return [
        ...((publications || []).map((item: any) => ({ id: `pub-${item.id}`, type: "Publication", label: item.status, detail: item.article_id, at: item.updated_at || item.published_at }))),
        ...((jobs || []).map((item: any) => ({ id: `job-${item.id}`, type: "Autopublish", label: item.status, detail: item.error_message, at: item.completed_at || item.updated_at || item.scheduled_for }))),
        ...((logs || []).map((item: any) => ({ id: `log-${item.id}`, type: "Automation", label: item.step, detail: item.error_message || item.status, at: item.created_at }))),
      ].filter((item) => item.at).sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()).slice(0, 30);
    },
    enabled: !!id,
  });

  if (siteLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!website) {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Publication introuvable</p>
        <button onClick={() => navigate("/websites")} className="text-sm text-primary hover:underline">
          ← Retour aux sites
        </button>
      </div>
    );
  }

  const publishedCount = articles.filter((a) => a.status === "published").length;
  const draftCount = articles.filter((a) => a.status === "draft").length;
  const scheduledCount = articles.filter((a) => a.status === "scheduled").length;
  const ownerLabel = ownerName || "Unknown";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="flex h-14 items-center gap-4 px-6">
          <button onClick={() => navigate("/websites")} className="text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-3 flex-1">
            {website.logo_url ? (
              <img src={website.logo_url} alt="" className="h-8 w-8 rounded-lg object-cover" />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent">
                <Globe className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
            <div>
              <h1 className="text-lg font-semibold text-foreground">{website.name}</h1>
              <p className="text-xs text-muted-foreground">{website.domain || `${website.slug}.delapresse.com`}</p>
            </div>
          </div>
          <Badge variant="outline" className={statusColors[website.status] || statusColors.draft}>
            {website.status}
          </Badge>
          {website.domain && (
            <a href={`https://${website.domain}`} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
      </header>

      <div className="p-6 space-y-6 animate-slide-in">
        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={FileText} label="Articles" value={website.article_count} />
          <StatCard icon={Eye} label="Publiés" value={publishedCount} />
          <StatCard icon={Clock} label="Brouillons" value={draftCount} />
          <StatCard icon={TrendingUp} label="Programmés" value={scheduledCount} />
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent">
              <UserCircle className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Owner</p>
              <p className="text-sm font-semibold text-foreground">{ownerLabel}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="articles" className="w-full">
          <TabsList>
            <TabsTrigger value="articles">Articles</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="hosting">Hosting</TabsTrigger>
            <TabsTrigger value="info">Informations</TabsTrigger>
            <TabsTrigger value="config">Configuration</TabsTrigger>
          </TabsList>

          {/* Articles tab */}
          <TabsContent value="articles" className="space-y-4">
            <FrontPagePinCard siteId={website.id} />
            <ShowcaseCard siteId={website.id} />
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Articles récents</CardTitle>
              </CardHeader>
              <CardContent>
                {articles.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">Aucun article pour le moment</p>
                ) : (
                  <div className="divide-y divide-border">
                    {articles.map((article) => (
                      <div
                        key={article.id}
                        onClick={() => navigate(`/articles/${article.id}`)}
                        className="flex items-center justify-between py-3 cursor-pointer hover:bg-accent/30 -mx-6 px-6 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-foreground truncate">{article.title}</p>
                            {article.is_featured && (
                              <Badge variant="outline" className="text-[10px] border-primary/30 text-primary">Featured</Badge>
                            )}
                            {article.is_breaking && (
                              <Badge variant="destructive" className="text-[10px]">Breaking</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            {article.reading_time && (
                              <span className="text-xs text-muted-foreground">{article.reading_time} min</span>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(article.created_at), "dd MMM yyyy", { locale: fr })}
                            </span>
                          </div>
                        </div>
                        <Badge variant="outline" className={`text-[10px] ${statusColors[article.status] || ""}`}>
                          {article.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="activity">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Activity className="h-4 w-4" />
                  Activite recente
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activityItems.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">Aucune activite pour le moment</p>
                ) : (
                  <div className="divide-y divide-border">
                    {activityItems.map((item: any) => (
                      <div key={item.id} className="flex items-center justify-between py-3">
                        <div>
                          <p className="text-sm font-medium text-foreground">{item.type}: {item.label}</p>
                          {item.detail && <p className="mt-0.5 text-xs text-muted-foreground">{item.detail}</p>}
                        </div>
                        <span className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(item.at), { addSuffix: true, locale: fr })}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="hosting">
            <HostingConnectionPanel websiteId={website.id} />
          </TabsContent>

          {/* Info tab */}
          <TabsContent value="info">
            <Card>
              <CardContent className="pt-6 space-y-4">
                <InfoRow label="Nom" value={website.name} />
                <InfoRow label="Owner" value={ownerLabel} />
                <InfoRow label="External hosting" value={website.external_hosting_provider ? `${website.external_hosting_provider} configured` : "Optional, not configured"} />
                <InfoRow label="Publish status" value={website.publish_status || "not_published"} />
                <InfoRow label="Sync status" value={website.sync_status || "not_synced"} />
                <InfoRow label="Last publish" value={website.last_publish_at ? formatDistanceToNow(new Date(website.last_publish_at), { addSuffix: true, locale: fr }) : "—"} />
                <InfoRow label="Last sync" value={website.last_sync_at ? formatDistanceToNow(new Date(website.last_sync_at), { addSuffix: true, locale: fr }) : "—"} />
                {(website.last_publish_error || website.last_sync_error) && <InfoRow label="Last error" value={website.last_publish_error || website.last_sync_error || "—"} />}
                <InfoRow label="Slug" value={website.slug} />
                <InfoRow label="Domaine" value={website.domain || "—"} />
                <InfoRow label="Slogan" value={website.tagline || "—"} />
                <InfoRow label="Description" value={website.description || "—"} />
                <InfoRow label="Niche" value={website.niche || "—"} />
                <InfoRow label="Template" value={website.template_id || "—"} />
                <InfoRow
                  label="Langues"
                  value={website.languages.map((l) => l.toUpperCase()).join(", ")}
                />
                <InfoRow label="Langue principale" value={website.primary_language.toUpperCase()} />
                <InfoRow
                  label="Créé le"
                  value={format(new Date(website.created_at), "dd MMMM yyyy 'à' HH:mm", { locale: fr })}
                />
                <InfoRow
                  label="Mis à jour"
                  value={formatDistanceToNow(new Date(website.updated_at), { addSuffix: true, locale: fr })}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Config tab */}
          <TabsContent value="config">
            <div className="grid gap-4 md:grid-cols-2">
              <ConfigCard title="Thème" config={website.theme_config} />
              <ConfigCard title="Contenu" config={website.content_config} />
              <ConfigCard title="IA" config={website.ai_config} />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: number }) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
        <div>
          <p className="text-2xl font-bold text-foreground">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-4">
      <span className="w-36 shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground">{value}</span>
    </div>
  );
}

function ConfigCard({ title, config }: { title: string; config: Record<string, unknown> | null }) {
  const entries = config ? Object.entries(config) : [];
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <p className="text-xs text-muted-foreground">Aucune configuration</p>
        ) : (
          <div className="space-y-1.5">
            {entries.map(([key, val]) => (
              <div key={key} className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{key}</span>
                <span className="text-foreground font-medium">{String(val)}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
